<template>
    <md-dialog md-open-from="#affiliateInstitution" md-close-to="#affiliateInstitution" ref="affiliateInstitution" @open="fixContent" @close="startOver" class="affiliate-institution-modal" :class="{ 'show-md-dark-mode': showMode }">
        <md-dialog-title>
            <div class="modal-title">Institutional Affiliation</div>
            <div class="close_modal" @click="cancel">&times;</div>
        </md-dialog-title>
        <md-dialog-content class="affiliate-institution-modal-content">
            <div class="select-your-institution">
                <div v-if="currentUid" class="ai-current">
                    <div class="ai-caption">Currently affiliated with</div>
                    <div class="ai-current-row">
                        <span class="ai-current-name" :class="{ 'ai-pending-remove': institution === '' }">{{ currentName || 'Your institution' }}</span>
                        <button v-if="institution !== ''" type="button" class="ai-link" @click="institution = ''">Remove</button>
                        <button v-else type="button" class="ai-link" @click="institution = currentUid">Undo</button>
                    </div>
                    <div v-if="institution === ''" class="ai-hint">Will be removed when you confirm.</div>
                </div>

                <label class="ai-caption" for="ai-search">{{ currentUid ? 'Change institution' : 'Find your institution' }}</label>
                <input id="ai-search" v-model="query" class="ai-search" type="search" placeholder="Search by name" autocomplete="off" @input="onQuery">

                <div v-if="queryTooShort" class="ai-status">Type at least 3 letters</div>
                <div v-else-if="searching" class="ai-status">Searching&hellip;</div>
                <ul v-else-if="results.length" class="ai-results" role="listbox">
                    <li v-for="r in results" :key="r.uid" role="option" :aria-selected="institution === r.uid" :class="{ selected: institution === r.uid }" @click="institution = r.uid">
                        <span class="ai-radio"></span>
                        <span class="ai-name">{{ r.institution_name }}</span>
                        <span v-if="institution === r.uid" class="ai-check">&#10003;</span>
                    </li>
                </ul>
                <div v-else-if="searchedFor" class="ai-status">No institutions match &ldquo;{{ searchedFor }}&rdquo;.</div>

                <div class="select-your-institution-fields ai-actions">
                    <md-button class="md-raised" @click="changeInstitution">Confirm Changes</md-button>
                </div>
                <p>This is how your institution will find you. Once you have chosen your institution, your Cuelist administrator will be able to activate your account.</p>
                <p>If you don't see your institution listed here, please reach out to your local administrator or <a href="mailto:info@thecuelist.com">contact</a> Cuelist with any questions or concerns.</p>
            </div>
        </md-dialog-content>
    </md-dialog>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import firebase from 'firebase/app';
import 'firebase/functions';

// Institution search, 23 Sept 2026 (rules plan S4, checklist 54t). This was a
// dropdown built from a live binding on the WHOLE users collection: every browser
// downloaded ~7,400 user records to show ~89 names, and saw every institution
// that had ever used Cuelist. The searchInstitutions callable now returns only
// matches ({uid, institution_name}), 3+ letters, hosts with paid access only.
//
// It also never returns the caller's own institution. Picking yourself sets
// affiliateInstitution to your own uid, which the sync-access gate
// (resolveSyncBillingSubject) reads as "seat holder, defer to the parent" and
// silently hides your OWN entitlement. Confirmed live 2026-08-18. That rule now
// lives in functions/lib/institution-search.js.
const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

const AffiliateInstitution = {
    name: 'affiliateInstitution',
    data: function() {
        return {
            institution: '',
            openedInstitution: '',
            opened: false,
            currentUid: '',
            currentName: '',
            query: '',
            results: [],
            searching: false,
            searchedFor: '',
            searchSeq: 0,
            debounceTimer: null
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'showMode'
        ]),
        // Live-bound signal: the member's own affiliation, which the admin (via
        // a Cloud Function) can change while this modal is open.
        liveAffiliateInstitution() {
            return (this.user && this.user.affiliateInstitution) || '';
        },
        queryTooShort() {
            const n = this.query.trim().length;
            return n > 0 && n < MIN_QUERY;
        }
    },
    watch: {
        liveAffiliateInstitution(newVal) {
            // If the member's affiliation changed elsewhere (e.g. the admin
            // removed them) and they haven't picked a different option, keep
            // the selection in sync — no refresh needed.
            if (this.opened && this.institution === this.openedInstitution) {
                this.institution = newVal;
            }
            this.openedInstitution = newVal;
            this.loadCurrent();
        }
    },
    methods: {
        open() {
            this.$refs.affiliateInstitution.open();
        },
        closeDialog() {
            this.$refs.affiliateInstitution.close();
        },
        fixContent() {
            this.opened = true;
            this.institution = this.user.affiliateInstitution || '';
            this.openedInstitution = this.institution;
            this.query = '';
            this.results = [];
            this.searchedFor = '';
            this.loadCurrent();
            Vue.material.setCurrentTheme(this.showMode ? 'show-mode' : 'default');
        },
        startOver() {
            this.opened = false;
            clearTimeout(this.debounceTimer);
            Vue.material.setCurrentTheme(this.showMode ? 'show-mode' : 'default');
        },
        cancel() {
            this.$refs.affiliateInstitution.close();
        },
        async loadCurrent() {
            const uid = (this.user && this.user.affiliateInstitution) || '';
            this.currentUid = uid;
            this.currentName = '';
            if (!uid) return;
            try {
                const res = await firebase.functions().httpsCallable('searchInstitutions')({ current: true });
                const current = res && res.data && res.data.current;
                if (current && current.uid === this.currentUid) this.currentName = current.institution_name;
            } catch (e) {
                console.error('[institution search] current:', e && e.message);
            }
        },
        onQuery() {
            clearTimeout(this.debounceTimer);
            const q = this.query.trim();
            if (q.length < MIN_QUERY) {
                this.results = [];
                this.searching = false;
                this.searchedFor = '';
                return;
            }
            this.searching = true;
            this.debounceTimer = setTimeout(() => this.runSearch(q), DEBOUNCE_MS);
        },
        async runSearch(q) {
            // Only the newest search may write results; a slow earlier reply must
            // not overwrite a later one.
            const seq = ++this.searchSeq;
            try {
                const res = await firebase.functions().httpsCallable('searchInstitutions')({ query: q });
                if (seq !== this.searchSeq) return;
                this.results = (res && res.data && res.data.results) || [];
            } catch (e) {
                if (seq !== this.searchSeq) return;
                console.error('[institution search]', e && e.message);
                this.results = [];
            }
            this.searchedFor = q;
            this.searching = false;
        },
        changeInstitution() {
            if (!this.opened) return;
            this.$nextTick(() => {
                this.$store.dispatch('updateUser', {
                    uid: this.user.uid,
                    update: {
                        affiliateInstitution: this.institution
                    }
                }).then(() => {
                    this.cancel();
                    this.$toast.success('Affiliate Institution updated successfully.');
                });
            });
        }
    }
};
export default AffiliateInstitution;
</script>

<style lang="scss">
/* Institutional Affiliation modal — restyled to match the account redesign.
   Local tokens; dark mode via .show-md-dark-mode on the dialog root. */
.affiliate-institution-modal {
    --navy: #0d2744;
    --accent: #73d5e3;
    --accent-ink: #12718a;
    --surface: #ffffff;
    --surface-2: #f6f9fc;
    --border: #dde4ee;
    --border-strong: #c9d4e2;
    --text: #13263e;
    --text-muted: #5c6d84;
    --btn-bg: #0d2744;
    --btn-fg: #ffffff;
    --btn-hover: #16375f;
    --danger: #c0392b;
    --ring: 0 0 0 3px rgba(115,213,227,.55);
}
.affiliate-institution-modal.show-md-dark-mode {
    --accent-ink: #73d5e3;
    --surface: #12243a;
    --surface-2: #0e1d30;
    --border: #24384f;
    --border-strong: #33506e;
    --text: #e9f1f8;
    --text-muted: #9fb4cb;
    --btn-bg: #73d5e3;
    --btn-fg: #08202c;
    --btn-hover: #8fe0eb;
    --danger: #f0938c;
}

.affiliate-institution-modal .md-dialog {
    max-width: 560px;
    background: var(--surface);
    color: var(--text);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 24px 70px rgba(6,15,26,.45);
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.affiliate-institution-modal .md-dialog-title {
    background: var(--navy);
    color: #fff;
    text-align: left;
    padding: 18px 24px;
    position: relative;
}
.affiliate-institution-modal .md-dialog-title .modal-title { font-size: 19px; font-weight: 700; line-height: 1.3; letter-spacing: .01em; }
.affiliate-institution-modal .close_modal {
    position: absolute; top: 10px; right: 12px;
    width: 34px; height: 34px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,.85); font-size: 24px; line-height: 1; cursor: pointer;
}
.affiliate-institution-modal .close_modal:hover { background: rgba(255,255,255,.12); color: #fff; }

.affiliate-institution-modal .affiliate-institution-modal-content {
    padding: 24px;
    background: var(--surface);
    color: var(--text);
}

/* Current affiliation, search field and results list (23 Sept 2026) */
.affiliate-institution-modal .ai-caption {
    display: block;
    font-size: 11px; letter-spacing: .04em; text-transform: uppercase;
    color: var(--text-muted); font-weight: 600; margin: 0 0 6px;
}
.affiliate-institution-modal .ai-current {
    border: 1px solid var(--border); border-radius: 9px;
    background: var(--surface-2); padding: 12px 14px; margin: 0 0 18px;
}
.affiliate-institution-modal .ai-current-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.affiliate-institution-modal .ai-current-name { font-size: 15px; font-weight: 600; color: var(--text); overflow-wrap: anywhere; }
.affiliate-institution-modal .ai-current-name.ai-pending-remove { text-decoration: line-through; color: var(--text-muted); }
.affiliate-institution-modal .ai-hint { font-size: 13px; color: var(--text-muted); margin-top: 6px; }
.affiliate-institution-modal .ai-link {
    background: none; border: none; padding: 4px 2px; cursor: pointer;
    color: var(--accent-ink); font-size: 14px; font-weight: 600; flex: 0 0 auto;
}
.affiliate-institution-modal .ai-link:focus-visible { outline: none; box-shadow: var(--ring); border-radius: 4px; }
.affiliate-institution-modal .ai-search {
    width: 100%; box-sizing: border-box;
    border: 1px solid var(--border-strong); border-radius: 9px;
    background: var(--surface-2); color: var(--text);
    padding: 12px 14px; font-size: 16px; outline: none;
}
.affiliate-institution-modal .ai-search:focus { box-shadow: var(--ring); border-color: var(--accent); }
.affiliate-institution-modal .ai-status { font-size: 14px; color: var(--text-muted); margin: 10px 2px 0; }
.affiliate-institution-modal .ai-results {
    list-style: none; margin: 10px 0 0; padding: 0;
    border: 1px solid var(--border); border-radius: 9px; overflow: hidden;
}
.affiliate-institution-modal .ai-results li {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px; cursor: pointer; font-size: 15px; color: var(--text);
    background: var(--surface);
}
.affiliate-institution-modal .ai-results li + li { border-top: 1px solid var(--border); }
.affiliate-institution-modal .ai-results li:hover { background: var(--surface-2); }
.affiliate-institution-modal .ai-results li.selected { background: var(--surface-2); font-weight: 600; }
.affiliate-institution-modal .ai-radio {
    width: 14px; height: 14px; border-radius: 50%; flex: 0 0 auto;
    border: 2px solid var(--border-strong); box-sizing: border-box;
}
.affiliate-institution-modal .ai-results li.selected .ai-radio { border: 4px solid var(--accent-ink); }
.affiliate-institution-modal .ai-name { flex: 1 1 auto; overflow-wrap: anywhere; }
.affiliate-institution-modal .ai-check { color: var(--accent-ink); font-weight: 700; }
.affiliate-institution-modal .ai-actions { margin-top: 18px; justify-content: flex-end; }

/* Confirm button */
.affiliate-institution-modal .select-your-institution-fields .md-button {
    flex: 0 0 auto;
    height: auto;
    min-width: 0;
    margin: 0;
    padding: 12px 20px;
    border-radius: 9px;
    background: var(--btn-bg);
    color: var(--btn-fg) !important;
    font-size: 14px;
    font-weight: 650;
    text-transform: none;
    box-shadow: none !important;
    border: 1px solid transparent;
}
.affiliate-institution-modal .select-your-institution-fields .md-button:hover { background: var(--btn-hover); }

.affiliate-institution-modal .select-your-institution p {
    color: var(--text-muted);
    margin: 14px 0 0;
    font-size: 14px;
    line-height: 1.55;
}
.affiliate-institution-modal .select-your-institution p a { color: var(--accent-ink); }

.affiliate-institution-modal .md-error { color: var(--danger); font-size: 12px; }
</style>


// WEBPACK FOOTER //
// affiliate-institution.vue?803d2394